
import React, { useState } from 'react';
import { ScanFace, Loader2, Plus, Sparkles, Check } from 'lucide-react';
import { MirrorAnalysis } from '../types';
import { analyzeTextReflection } from '../services/geminiService';
import { useTheme } from '../contexts/ThemeContext';
import { BaseModal } from './BaseModal';

interface Props {
  onClose: () => void;
  onMerge: (data: MirrorAnalysis) => void;
}

export const MirrorAnalysisModal: React.FC<Props> = ({ onClose, onMerge }) => {
  const [inputText, setInputText] = useState('');
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [result, setResult] = useState<MirrorAnalysis | null>(null);
  const { theme } = useTheme();

  const handleAnalyze = async () => {
    if (!inputText.trim()) return;
    setIsAnalyzing(true);
    try {
        const data = await analyzeTextReflection(inputText);
        setResult(data);
    } catch (e) {
        console.error("Analysis failed", e);
    } finally {
        setIsAnalyzing(false);
    }
  };

  const handleMerge = () => {
      if (result) {
          onMerge(result);
          onClose();
      }
  };

  return (
    <BaseModal
        onClose={onClose}
        title="The 360° Mirror"
        icon={<ScanFace size={16} />}
        headerColor={theme.colors.features.mirror}
        maxWidth="max-w-4xl"
    >
            {!result ? (
                <div className="flex flex-col h-full max-w-2xl mx-auto py-6">
                    <div className={`rounded-xl p-4 mb-6 flex items-start gap-3 border ${theme.colors.status.info}`}>
                        <Sparkles size={20} className="shrink-0 mt-0.5" />
                        <p className="text-sm">
                            We are often terrible narrators of our own lives. Paste a resume, cover letter, journal entry, or even a LinkedIn bio. Kai will analyze implicit patterns to find Ikigai drivers you might be missing.
                        </p>
                    </div>

                    <div className="flex-1 flex flex-col gap-2">
                        <label className={`text-sm font-bold uppercase tracking-wide ${theme.colors.text.secondary}`}>Your Text / Resume / Journal</label>
                        <textarea 
                            value={inputText}
                            onChange={(e) => setInputText(e.target.value)}
                            placeholder="Paste text here..."
                            className={`flex-1 w-full p-4 rounded-xl focus:outline-none focus:ring-2 focus:ring-cyan-500 transition-all resize-none min-h-[300px] text-sm leading-relaxed border ${theme.colors.input.bg} ${theme.colors.input.border} ${theme.colors.input.text}`}
                        />
                    </div>

                    <button 
                        onClick={handleAnalyze}
                        disabled={!inputText.trim() || isAnalyzing}
                        className={`w-full mt-6 py-4 font-bold rounded-xl transition-colors shadow-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 ${theme.mode === 'dark' ? 'bg-cyan-600 hover:bg-cyan-500 text-white' : 'bg-cyan-600 hover:bg-cyan-700 text-white'}`}
                    >
                        {isAnalyzing ? <Loader2 size={20} className="animate-spin" /> : <ScanFace size={20} />}
                        {isAnalyzing ? 'Analyzing Reflection...' : 'Analyze Reflection'}
                    </button>
                </div>
            ) : (
                <div className="space-y-8 animate-scale-up p-6">
                    <div className="flex items-center justify-between">
                         <h3 className={`text-lg font-bold flex items-center gap-2 ${theme.colors.text.main}`}>
                            <Sparkles className="text-cyan-500" size={20}/> Reflection Results
                         </h3>
                         <button onClick={() => setResult(null)} className={`text-sm underline ${theme.colors.text.secondary} hover:text-cyan-500`}>
                             Analyze different text
                         </button>
                    </div>

                    {/* Blind Spots (Highlight) */}
                    <div className="bg-slate-800 text-slate-100 p-6 rounded-2xl shadow-lg border border-slate-700">
                         <h4 className="font-bold text-cyan-400 mb-3 flex items-center gap-2">
                            <ScanFace size={18} /> Detected Blind Spots
                         </h4>
                         <ul className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            {result.blindSpots.map((spot, i) => (
                                <li key={i} className="flex items-start gap-2 text-sm text-slate-300">
                                    <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-cyan-500 shrink-0"></span>
                                    {spot}
                                </li>
                            ))}
                         </ul>
                    </div>

                    {/* 4 Pillars Grid - UPDATED TITLES */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <ResultCard title="Love It (Passion)" items={result.passion} theme={theme} bucket="passion" />
                        <ResultCard title="World Needs (Mission)" items={result.mission} theme={theme} bucket="mission" />
                        <ResultCard title="Good At (Profession)" items={result.profession} theme={theme} bucket="profession" />
                        <ResultCard title="Making Money (Vocation)" items={result.vocation} theme={theme} bucket="vocation" />
                    </div>

                    <div className="flex justify-center pt-4">
                        <button 
                            onClick={handleMerge}
                            className={`flex items-center gap-2 px-8 py-3 rounded-full font-bold transition-colors shadow-lg hover:shadow-xl hover:-translate-y-1 transform ${theme.components.button.primary}`}
                        >
                            <Check size={20} />
                            Merge Findings to Dashboard
                        </button>
                    </div>
                </div>
            )}
    </BaseModal>
  );
};

const ResultCard: React.FC<{ title: string; items: string[]; theme: any; bucket: 'passion' | 'mission' | 'profession' | 'vocation' }> = ({ title, items, theme, bucket }) => {
    const style = theme.colors.buckets[bucket];
    return (
        <div className={`border rounded-xl p-4 ${style.bg} ${style.border}`}>
            <h4 className={`font-bold mb-2 uppercase text-xs tracking-wider ${style.text}`}>{title}</h4>
            {items.length > 0 ? (
                <ul className="space-y-1.5">
                    {items.map((item: string, i: number) => (
                        <li key={i} className={`text-sm flex items-start gap-2 ${theme.mode === 'dark' ? 'text-slate-300' : 'text-gray-700'}`}>
                            <Plus size={12} className={`mt-1 shrink-0 ${style.icon} opacity-50`} />
                            <span>{item}</span>
                        </li>
                    ))}
                </ul>
            ) : (
                <p className={`text-xs italic ${theme.colors.text.secondary}`}>No implicit traits detected.</p>
            )}
        </div>
    );
}
